/**
 * StatusButton component for the Indigo design system.
 *
 * A `Button` that carries the state of whatever it opens — a form section, a
 * questionnaire, a review step — as a trailing indicator. It is the entry
 * point pattern for lists of sub-tasks: the label says where the button goes,
 * the indicator says whether it still needs the user.
 *
 * Every `Button` prop passes straight through, so variant, size, icon and
 * loading behave exactly as they do on `Button`.
 *
 * Status colors follow the design system's status mapping: yellow = action
 * needed from you, blue = waiting on someone else, green = done, red = error,
 * gray = nothing to do.
 *
 * @example
 * ```tsx
 * <StatusButton
 *   variant="secondary"
 *   icon={<Icon name="clipboard-list" />}
 *   status="attention"
 *   onClick={openOnboardingQuestions}
 * >
 *   Onboarding questions
 * </StatusButton>
 * ```
 */
import { clsx } from "clsx";
import { forwardRef } from "react";
import { Button, type ButtonProps } from "../Button";
import { Icon, type IconProps } from "../Icons";
import { Tooltip } from "../Tooltip";
import "./StatusButton.css";

/**
 * The state a StatusButton can report.
 * - `todo`: not started, nothing blocking (gray)
 * - `attention`: needs action from the current user (yellow)
 * - `in-progress`: partially filled in (yellow)
 * - `waiting`: pending on another party (blue)
 * - `complete`: done, no action needed (green)
 * - `error`: failed or invalid, needs fixing (red)
 */
export type ButtonStatus =
  | "todo"
  | "attention"
  | "in-progress"
  | "waiting"
  | "complete"
  | "error";

/** Icon shown for each status in `indicator="icon"` mode. */
const STATUS_ICON: Record<ButtonStatus, IconProps["name"]> = {
  todo: "functional-missing-reversed",
  attention: "functional-warning-triangle-reversed",
  "in-progress": "functional-partial-reversed",
  waiting: "functional-check-blue-reversed",
  complete: "functional-check-green-reversed",
  error: "functional-failed-reversed",
};

/** Dot color for each status in `indicator="dot"` mode. */
const STATUS_TONE: Record<ButtonStatus, string> = {
  todo: "neutral",
  attention: "warning",
  "in-progress": "warning",
  waiting: "info",
  complete: "success",
  error: "danger",
};

/**
 * Fallback accessible name for the indicator, so the status is never
 * color-only. Override with `statusLabel` when the domain has a better word.
 */
const STATUS_LABEL: Record<ButtonStatus, string> = {
  todo: "Not started",
  attention: "Needs attention",
  "in-progress": "In progress",
  waiting: "Waiting on others",
  complete: "Complete",
  error: "Error",
};

export interface StatusButtonProps extends Omit<ButtonProps, "tag"> {
  /**
   * State reported by the trailing indicator. Omit it and the button renders
   * exactly like a plain `Button`.
   */
  status?: ButtonStatus;

  /**
   * How the status is drawn: a filled status icon, or a small colored dot.
   * @default "icon"
   */
  indicator?: "icon" | "dot";

  /**
   * Accessible name for the indicator. Defaults to a generic label for the
   * status ("Needs attention", "Complete", …).
   */
  statusLabel?: string;

  /**
   * Tooltip shown on hover/focus of the indicator — e.g. "3 questions left".
   * The indicator has an accessible name either way; this is the extra detail.
   */
  statusTooltip?: React.ReactNode;

  /**
   * Stretches the button to the width of its container and pushes the
   * indicator to the trailing edge, for a stacked list of entry points.
   * @default false
   */
  fullWidth?: boolean;
}

export const StatusButton = forwardRef<HTMLButtonElement, StatusButtonProps>(
  (
    {
      status,
      indicator = "icon",
      statusLabel,
      statusTooltip,
      fullWidth = false,
      children,
      className,
      ...buttonProps
    },
    ref,
  ) => {
    const renderIndicator = () => {
      if (!status) {
        return null;
      }

      const label = statusLabel ?? STATUS_LABEL[status];

      const node = (
        <span
          className={clsx(
            "indigo-status-button__status",
            indicator === "dot" &&
              `indigo-status-button__dot indigo-status-button__dot--${STATUS_TONE[status]}`,
          )}
          role="img"
          aria-label={label}
        >
          {indicator === "icon" && <Icon name={STATUS_ICON[status]} size="s" />}
        </span>
      );

      if (!statusTooltip) {
        return node;
      }

      return (
        <Tooltip content={statusTooltip} placement="top" delay={200}>
          {node}
        </Tooltip>
      );
    };

    return (
      <Button
        ref={ref}
        className={clsx(
          "indigo-status-button",
          fullWidth && "indigo-status-button--full",
          className,
        )}
        {...buttonProps}
      >
        {children}
        {renderIndicator()}
      </Button>
    );
  },
);

StatusButton.displayName = "StatusButton";

export default StatusButton;
